import { i18nHelper } from '@tweddle-se-team/common/helpers'
import React, { useMemo, useEffect, useCallback } from 'react'
import { Nav, Dropdown } from 'rsuite'
import { FaCar } from 'react-icons/fa6'
import { FaUserCircle } from 'react-icons/fa'
import { useDispatch, useSelector } from 'react-redux'
import Router, { useRouter } from 'next/router'
import { Container } from '@/components/styles'
import { configSelectors, configsActions } from '@/store/configs'
import { i18nL10nSelectors, i18nL10nActions } from '@/store/i18nL10n'
import { loggedInUserActions, loggedInUserSelector } from '@/store/loggedInUser'
import { productSelectors, productActions } from '@/store/products'
import { searchActions } from '@/store/search'
import NavigationItems from './navigationItems'
import navigationOptions from './headerHelpers'
import HeaderBrand from './headerBrand'
import { CustomNavbar, CustomNavIcoItem, CustomDropdown } from './headerElements'
import HeaderLanguage from './headerLanguage'
import { proxyInstance } from '@/hooks/useApi'

const { setLocale } = i18nHelper

const Header = () => {
  const router = useRouter()
  const dispatch = useDispatch()
  const { isOnline, isInFrame, isInApp, showStaticLogo } = useSelector(configSelectors.selectConfigs)
  const locale = useSelector(i18nL10nSelectors.selectLocale)
  const filterBy = useSelector(productSelectors.selectFilterBy)
  const { brand: brands } = useSelector(productSelectors.selectOptionsPerFilter)
  const loggedInUser = useSelector(loggedInUserSelector.selectUser)
  const signedInAsAdmin = useSelector(loggedInUserSelector.selectHasRole('Admin'))
  const signedIn = loggedInUser && loggedInUser._id

  const changeLocale = useCallback(
    async locale => {
      setLocale(locale)
      dispatch(i18nL10nActions.SET_LOCALE(locale))
      router.push('/')
    },
    [dispatch, router]
  )

  const login = useCallback(() => router.push('/login'), [router])

  const { logOut, returnToLandingPage, onBrandChange, setIsIframe } = useMemo(
    () => getStoreActions(dispatch),
    [dispatch]
  )

  useEffect(() => setIsIframe(window.self !== window.top))

  return (
    <div style={{ display: isInFrame || isInApp ? 'none' : 'block' }}>
      <CustomNavbar appearance="subtle">
        <Container>
          <HeaderBrand
            brands={brands}
            filterBy={filterBy}
            isOnline={isOnline}
            showStaticLogo={showStaticLogo}
            onBrandChange={brand => onBrandChange(brand, () => router.push('/'))}
          />
          <Nav pullRight>
            <CustomNavIcoItem id="home" onClick={e => returnToLandingPage(e, isOnline)}>
              <FaCar size={24} />
            </CustomNavIcoItem>
            {signedIn && isOnline && (
              <CustomDropdown
                icon={<FaUserCircle size={24} />}
                placement="bottomEnd"
                eventKey={7}
                noCaret
                id="user_profile"
              >
                <Nav.Item>
                  <p>Signed in as: </p>
                  <strong>{loggedInUser.userName}</strong>
                </Nav.Item>
                <Dropdown.Separator />
                {signedInAsAdmin && <NavigationItems router={router} items={navigationOptions.admin} />}

                <Nav.Item onClick={logOut}>Log out</Nav.Item>
              </CustomDropdown>
            )}
            {!signedIn && isOnline && !showStaticLogo && (
              <CustomNavIcoItem onClick={login} id="user_profile">
                <FaUserCircle size={24} />
              </CustomNavIcoItem>
            )}
            <HeaderLanguage locale={locale} changeLocale={changeLocale} />
          </Nav>
        </Container>
      </CustomNavbar>
    </div>
  )
}

const getStoreActions = dispatch => ({
  logOut: async () => {
    await proxyInstance.post('auth/logout')
    dispatch(loggedInUserActions.RESET())
    window.location.replace('/login')
  },
  returnToLandingPage: (e, isOnline) => {
    e.preventDefault()
    isOnline ? Router.push('/').catch(console.error) : Router.push('/cachedPublications')
  },
  onBrandChange: (brand, callback = () => {}) => {
    dispatch(productActions.SET_BRAND(brand))
    dispatch(searchActions.SET_BRAND(brand))
    callback()
  },
  setIsIframe: isIframe => {
    dispatch(configsActions.SET_IS_IN_IFRAME(isIframe))
  }
})

export default Header
